import React, { useState, useEffect, useMemo } from "react";
import { USE_MOCK_DATA, useAppContext } from "../lib/mockState";
import { persistLocalPreviewAgentRun, USE_LOCAL_PREVIEW_SURFACE_DATA } from "../lib/localPreviewSurfaceStore";
import { generateAgentReportCallable, matchIntentCallable, runAgentTaskCallable } from "../lib/phase75Callables";
import { WorkspaceTask, ExecutionStep } from "../components/workspace/types";
import { WorkspaceTaskInput } from "../components/workspace/WorkspaceTaskInput";
import { ActionStream } from "../components/workspace/ActionStream";
import { PromptLibrary } from "../components/workspace/PromptLibrary";
import { useAgentRuns } from "../hooks/useLiveSurfaceRecords";
import { 
  Activity, 
  Search, 
  Filter, 
  Flame, 
  Sparkles, 
  ChevronRight, 
  Bot, 
  BookMarked, 
  ExternalLink, 
  Send, 
  HelpCircle, 
  CheckCircle, 
  FileCheck,
  AlertTriangle,
  Globe,
  Terminal
} from "lucide-react";

interface ExecutionPreviewState {
  title: string;
  summary: string;
  matterId: string;
  source: "freeform" | "template";
  prompt: string;
  taskLabel: string;
  taskId?: string;
  subject: "matter" | "workspace";
  params: Record<string, unknown>;
  optimisticTask?: WorkspaceTask;
}

function buildExecutionResult(preview: ExecutionPreviewState, queuedRunId?: string) {
  if (preview.source === "template" && /morning briefing/i.test(preview.title)) {
    return `Morning Briefing ready for ${preview.matterId}: deadlines reviewed, alerts checked, and KYC exposure summarized.`;
  }

  if (queuedRunId) {
    return `Execution result: queued agent run ${queuedRunId} for ${preview.matterId}.`;
  }

  return `Execution result: created a billable time entry plan for ${preview.matterId} and queued the workspace action for review.`;
}

export const AgentWorkspacePage: React.FC = () => {
  const { lang, matters, writeLogs } = useAppContext();
  const isAr = lang === "ar";
  const agentRuns = useAgentRuns();
  const [executionPreview, setExecutionPreview] = useState<ExecutionPreviewState | null>(null);
  const [executionResult, setExecutionResult] = useState<string | null>(null);
  const [isApprovingExecution, setIsApprovingExecution] = useState(false);
  const [pendingLiveRuns, setPendingLiveRuns] = useState<WorkspaceTask[]>([]);

  // Tab State matching CasePage and ContractPage style
  const [activeTab, setActiveTab] = useState<"Overview" | "ProcessStream" | "AI_Counsel">("Overview");

  // ROI metrics in state to show dynamic count increases
  const [tasksCount, setTasksCount] = useState(USE_MOCK_DATA ? 41 : 0);
  const [hoursSaved, setHoursSaved] = useState(USE_MOCK_DATA ? 114.5 : 0);
  const [recoveredAED, setRecoveredAED] = useState(USE_MOCK_DATA ? 64800 : 0);

  // Discrete filters state
  const [activeDateFilter, setActiveDateFilter] = useState<"TODAY" | "YESTERDAY" | "LAST_7_DAYS" | "ALL_TIME">("ALL_TIME");
  const [activeStatusFilter, setActiveStatusFilter] = useState<"ALL" | "COMPLETED" | "FAILED" | "RUNNING" | "QUEUED">("ALL");

  // Prompt Library modal state
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [agentPersistenceStatus, setAgentPersistenceStatus] = useState<string | null>(null);

  // Mini Consult Chat state
  const [chatInput, setChatInput] = useState("");
  const [chatHistory, setChatHistory] = useState<Array<{ sender: "user" | "bot"; text: string }>>([
    { 
      sender: "bot", 
      text: isAr 
        ? "أهلاً بك في استشارات الرقابة والالتزام. اسألني أي سؤال حول لوائح مركز دبي المالي أو المادة القانونية في القانون المدني الإماراتي."
        : "Welcome to AI Counsel Instant Consult. Ask me anything regarding DIFC Court rules or UAE Civil Transactions Code compliance." 
    }
  ]);

  // Tasks ledger state
  const [tasks, setTasks] = useState<WorkspaceTask[]>(USE_MOCK_DATA ? [
    {
      id: "task-01",
      title: "Notice Window Opt-out Compliance Assessment",
      prompt: "Audit corporate lease portfolios for CON-2026-0117. Scan terms to locate potential automatic expansion risks or un-notified milestones.",
      status: "COMPLETED",
      date: new Date(Date.now() - 3600000 * 24).toISOString(), // Yesterday
      matterId: "CON-2026-0117",
      matterName: "Acme Lease Agreement",
      executor: "Sofia",
      progress: 100,
      steps: [
        { id: "s1", name: "Parse Lease Portfolio", nameAr: "تحليل بنود عقد الإيجار", status: "completed", description: "Successfully extracted Article 12 termination specifications.", descriptionAr: "تم استخراج نطاق بنود المادة ١٢ للإنهاء بنجاح." },
        { id: "s2", name: "Calculate Expiration Frame", nameAr: "حساب نافذة انتهاء الالتزام", status: "completed", description: "Trigger constraints validated against municipal guidelines.", descriptionAr: "تم مطابقة قيود التنبيه مع المعايير البلدية." }
      ],
      hoursSaved: 3.5,
      recoveredAED: 4500,
      isReported: true
    },
    {
      id: "task-02",
      title: "Evidentiary Timeline Chronology Check",
      prompt: "Audit critical evidentiary chronological inconsistencies for CASE-2026-0042 (Smith v. Gulf Commercial). Look for delayed billing exhibits.",
      status: "FAILED",
      date: new Date(Date.now() - 3600000 * 3).toISOString(), // 3 hours ago
      matterId: "CASE-2026-0042",
      matterName: "Smith v. Gulf Commercial",
      executor: "Alex",
      progress: 35,
      steps: [
        { id: "x1", name: "Verify Digital Audit Trails", nameAr: "التحقق من سجلات الأمان الرقمي", status: "completed", description: "Scanned Smith case file attachments for cryptographic verification.", descriptionAr: "تم البحث في مرفقات سجل قضية سميث للمطابقة الرقمية." },
        { id: "x2", name: "Signature Authenticity Sync", nameAr: "مزامنة مطابقة صحة التواقيع", status: "failed", description: "CRITICAL REJECT: Delay specifications sheet (ev-02) doesn't have verified authority seal, blocking audit.", descriptionAr: "فشل حرج: جدول مواصفات التأخر لا يحتوي على ختم تفويض مصادق، مما يعوق التدقيق." }
      ],
      hoursSaved: 0,
      recoveredAED: 0,
      isReported: false
    },
    {
      id: "task-03",
      title: "Indemnity Cap Deviation Audit",
      prompt: "Cross-check limit of liability provisions inside Middle-East construction drafts. Guard against strict unapproved indemnification drafts.",
      status: "COMPLETED",
      date: new Date(Date.now() - 3600000 * 18).toISOString(), // 18 hours ago
      matterId: "CON-2026-0099",
      matterName: "DIFC Mall Construction Covenant",
      executor: "Sofia",
      progress: 100,
      steps: [
        { id: "y1", name: "OCR Draft Sections", nameAr: "المسح الضوئي لأجزاء العقد", status: "completed", description: "Contract draft segments converted to digital search tokens.", descriptionAr: "تم تحويل صياغة نسخة المسودة لبيانات باحثة مفهرسة." },
        { id: "y2", name: "Compare Core Limits Caps", nameAr: "مقارنة حدود التعويض الأساسية", status: "completed", description: "No liabilities departures detected based on DIFC §4.3 bounds.", descriptionAr: "لم يسجل أي حيود في المسؤولية التعاقدية وفق مركز دبي المالي." }
      ],
      hoursSaved: 12.0,
      recoveredAED: 18200,
      isReported: false
    }
  ] : []);

  const liveTasks: WorkspaceTask[] = useMemo(
    () => agentRuns.records.map((run) => ({ ...run })),
    [agentRuns.records],
  );
  const visibleTasks = USE_MOCK_DATA
    ? tasks
    : [...pendingLiveRuns.filter((run) => !liveTasks.some((liveRun) => liveRun.id === run.id)), ...liveTasks];
  const visibleTasksCount = USE_MOCK_DATA ? tasksCount : liveTasks.length;
  const visibleHoursSaved = USE_MOCK_DATA ? hoursSaved : liveTasks.reduce((total, task) => total + task.hoursSaved, 0);
  const visibleRecoveredAED = USE_MOCK_DATA ? recoveredAED : liveTasks.reduce((total, task) => total + task.recoveredAED, 0);

  // Task simulation scheduler
  useEffect(() => {
    const activeTaskIndex = tasks.findIndex(t => t.status === "QUEUED" || t.status === "RUNNING");
    if (activeTaskIndex === -1) return;
    const task = tasks[activeTaskIndex];

    const timer = setTimeout(() => {
      let isCompletedNow = false;
      let targetTask: any = null;

      setTasks(prev => {
        return prev.map(t => {
          if (t.id !== task.id) return t;

          if (t.status === "QUEUED") {
            const updatedSteps = [...t.steps];
            if (updatedSteps[0]) updatedSteps[0].status = "running";
            return {
              ...t,
              status: "RUNNING",
              progress: 20,
              steps: updatedSteps
            };
          }

          if (t.status === "RUNNING") {
            const runningStepIdx = t.steps.findIndex(s => s.status === "running");
            const updatedSteps = [...t.steps];

            if (runningStepIdx === -1) {
              if (updatedSteps[0]) updatedSteps[0].status = "running";
              return { ...t, progress: 25, steps: updatedSteps };
            }

            updatedSteps[runningStepIdx].status = "completed";

            if (runningStepIdx < updatedSteps.length - 1) {
              updatedSteps[runningStepIdx + 1].status = "running";
              return {
                ...t,
                progress: Math.min(t.progress + 35, 80),
                steps: updatedSteps
              };
            } else {
              isCompletedNow = true;
              targetTask = t;
              return {
                ...t,
                status: "COMPLETED",
                progress: 100,
                steps: updatedSteps
              };
            }
          }

          return t;
        });
      });

      if (isCompletedNow && targetTask) {
        setTasksCount(prev => prev + 1);
        setHoursSaved(prev => prev + targetTask.hoursSaved);
        setRecoveredAED(prev => prev + targetTask.recoveredAED);

        if (USE_MOCK_DATA) writeLogs(
          `${targetTask.executor.toUpperCase()}_AGENT`,
          "WORKSPACE_EXECUTION_COMPLETED",
          `Simulation task "${targetTask.title}" completed. ROI logged instantly.`,
          targetTask.matterId
        );
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [tasks]);

  // Handle manual dispatching from sticky Composer input
  const handleAddNewTask = async (promptText: string) => {
    const isConv = promptText.toLowerCase().includes("con-") || promptText.toLowerCase().includes("lease") || promptText.toLowerCase().includes("contract");
    const targetMatterId = isConv ? "CON-2026-0117" : "CASE-2026-0042";
    const matterName = isConv ? "Acme Lease Agreement" : "Smith v. Gulf Commercial";
    const executor = isConv ? "Sofia" : "Alex";

    const newTask: WorkspaceTask = {
      id: `task-${Date.now()}`,
      title: isConv ? "Contract Rule-Adherence Extraction" : "Ligation Proof-Integrity Search",
      prompt: promptText,
      status: "QUEUED",
      date: new Date().toISOString(),
      matterId: targetMatterId,
      matterName,
      executor,
      progress: 0,
      steps: [
        {
          id: "step-1",
          name: "Verify Local Venue Grounding",
          nameAr: "التحقق من مرجعية المقر المحلي",
          status: "pending",
          description: "Aligning text parameters with Middle-East legal precedent catalogs.",
          descriptionAr: "مطابقة أحكام النصوص مع كشاف السوابق واللوائح المحلية."
        },
        {
          id: "step-2",
          name: "Deep Clause Outliers Sweep",
          nameAr: "مسح الثغرات العميقة للغش والنزاع",
          status: "pending",
          description: "Scanning for critical compliance drifts and unauthorized liability limits.",
          descriptionAr: "البحث عن ثغرات الالتزام والانحرافات غير المعتمدة للتعويض."
        }
      ],
      hoursSaved: Math.round((Math.random() * 4 + 1.5) * 10) / 10,
      recoveredAED: Math.round((Math.random() * 8000 + 1500) / 100) * 100,
      isReported: false,
    };

    queueExecutionPreview(
      {
        title: newTask.title,
        summary: `Preview: ${newTask.title} will review ${targetMatterId} and can create a 1.0 hour client-call billing action if approved.`,
        matterId: targetMatterId,
        source: "freeform",
        prompt: promptText,
        taskLabel: newTask.title,
        subject: "matter",
        params: { caseId: targetMatterId, matterId: targetMatterId, auditSections: ["readiness", "evidence", "billing"] },
        optimisticTask: newTask,
      },
    );
  };

  // Handle launching from Prompt template modal dropdown
  const handleLaunchTemplate = async (data: {
    templateId: string;
    templateName: string;
    matterId: string;
    executor: string;
    scopes: string[];
    customCommand: string;
  }) => {
    const selectedMatter = matters.find(m => m.matterId === data.matterId) || matters[0];

    queueExecutionPreview(
      {
        title: data.templateName,
        summary: `${data.templateName} will run on ${data.matterId} across ${data.scopes.join(", ")}.`,
        matterId: data.matterId,
        source: "template",
        prompt: data.customCommand,
        taskId: data.templateId.includes("contract") ? "workspace_contract_expiry" : "workspace_case_audit",
        taskLabel: data.templateName,
        subject: "matter",
        params: { caseId: data.matterId, matterId: data.matterId, auditSections: data.scopes },
        optimisticTask: {
          id: `tpl-${Date.now()}`,
          title: `${data.templateName} (${data.scopes.length} Scopes)`,
          prompt: data.customCommand,
          status: "QUEUED",
          date: new Date().toISOString(),
          matterId: data.matterId,
          matterName: selectedMatter ? selectedMatter.title : "Corporate Contract Docket",
          executor: data.executor,
          progress: 0,
          steps: data.scopes.map((scopeName, idx) => ({
            id: `scr-${idx}`,
            name: `Scrutinize: ${scopeName}`,
            nameAr: `فحص تدقيق: ${scopeName}`,
            status: "pending",
            description: `Autonomous core verification segment targeted on ${scopeName} rules compliance.`,
            descriptionAr: `فحص مصادقة مستقل يستهدف الامتثال للوائح ${scopeName}.`
          })),
          hoursSaved: Math.round((Math.random() * 5 + 3.5) * 10) / 10,
          recoveredAED: Math.round((Math.random() * 12000 + 4000) / 100) * 100,
        },
      },
    );
  };

  // Handling failed retry triggers
  const handleRetryTask = (id: string) => {
    if (!USE_MOCK_DATA) {
      setAgentPersistenceStatus("Retry requires a server-owned agent run transition.");
      return;
    }
    setTasks(prev => {
      return prev.map(t => {
        if (t.id !== id) return t;
        // reset steps status to pending
        const resetSteps: Array<{ id: string; name: string; nameAr: string; status: "pending" | "running" | "completed" | "failed"; description: string; descriptionAr: string }> = t.steps.map(s => ({ ...s, status: "pending" as "pending" | "running" | "completed" | "failed" }));
        resetSteps[0].status = "running";
        return {
          ...t,
          status: "RUNNING" as const,
          progress: 10,
          steps: resetSteps
        };
      });
    });
    writeLogs("USER_CORES", "FLUSH_RETRY", `User restarted failed task execution: ${id}`);
  };

  // Handling report generation
  const handleReportTask = async (id: string) => {
    if (USE_MOCK_DATA) {
      setTasks(prev => {
        return prev.map(t => {
          if (t.id !== id) return t;
          return {
            ...t,
            isReported: true
          };
        });
      });
    }
    try {
      const run = visibleTasks.find((task) => task.id === id);
      const result = await generateAgentReportCallable({ title: `Agent report for ${id}`, runId: id, taskDocId: id, matterId: run?.matterId });
      setAgentPersistenceStatus(`Agent report ready: ${result.data.reportId}`);
    } catch (error) {
      setAgentPersistenceStatus(error instanceof Error ? error.message : "Report generated locally only.");
    }
    writeLogs("ALEX_CORES", "REPORT_GENERATED", `Constructed complete diagnostic report file for task execution ${id}`);
  };

  // Handling mini chat search consult
  const handleMiniChatSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatInput.trim()) return;

    const userText = chatInput;
    setChatHistory(prev => [...prev, { sender: "user", text: userText }]);
    setChatInput("");

    setTimeout(() => {
      // Return beautiful contextual mock response base on input query
      let answer = "";
      if (userText.toLowerCase().includes("difc")) {
        answer = isAr
          ? "توجيهات قواعد مركز دبي المالي المالي (DIFC Article 5) تفرض التزاماً كاملاً بصحة التوقيع الرقمي للمذكرات القضائية لضمان قبول الدعوى."
          : "Under DIFC Practice Directions, all electronic filing signatures require high-assurance digital verification. Lack of verified certificates leads to filing rejection under Art. 4.2.";
      } else if (userText.toLowerCase().includes("good faith") || userText.toLowerCase().includes("246")) {
        answer = isAr
          ? "المادة ٢٤٦ من القانون المدني الإماراتي تنص على وجوب تنفيذ العقد بحسن نية كاملة، مما يعني حظر تطبيق أي نوافذ شروط مخادعة."
          : "Article 246 of the UAE Civil Transactions Law establishes the overarching duty of Good Faith. AI checks verify that notice limits do not arbitrarily trigger unfair forfeitures.";
      } else {
        answer = isAr
          ? "تم تحليل استفسارك وتوصيله بدليل المحاكم والالتزام المحلي للإمارة لتزويدك بالصياغة البديلة المتوافقة."
          : "Consult synced with the local UAE legal database. Active recommendation has been updated for local construction litigation timelines.";
      }
      setChatHistory(prev => [...prev, { sender: "bot", text: answer }]);
    }, 1000);
  };

  // Filtering calculations based on Date and Status discrete chips
  const getFilteredTasks = () => {
    return visibleTasks.filter(task => {
      if (activeDateFilter === "TODAY") {
        const isToday = new Date(task.date).toDateString() === new Date().toDateString();
        if (!isToday) return false;
      } else if (activeDateFilter === "YESTERDAY") {
        const isYesterday = new Date(task.date).toDateString() === new Date(Date.now() - 3600000 * 24).toDateString();
        if (!isYesterday) return false;
      }
      if (activeStatusFilter !== "ALL") {
        if (task.status !== activeStatusFilter) return false;
      }
      return true;
    });
  };

  const filteredTasks = getFilteredTasks();

  useEffect(() => {
    if (USE_MOCK_DATA) return;
    setPendingLiveRuns((current) => {
      if (current.length === 0) return current;
      const next = current.filter((run) => !liveTasks.some((liveRun) => liveRun.id === run.id));
      return next.length === current.length ? current : next;
    });
  }, [liveTasks]);

  const queueExecutionPreview = (preview: ExecutionPreviewState) => {
    setExecutionPreview(preview);
    setExecutionResult(null);
    setAgentPersistenceStatus(`Execution preview ready: ${preview.title}`);
  };

  const buildQueuedRun = (preview: ExecutionPreviewState, runId: string) : WorkspaceTask => ({
    id: runId,
    title: preview.optimisticTask?.title ?? preview.title,
    prompt: preview.prompt,
    status: "QUEUED",
    date: new Date().toISOString(),
    matterId: preview.matterId,
    matterName: preview.optimisticTask?.matterName || matters.find((matter) => matter.matterId === preview.matterId)?.title || "Unlinked matter",
    executor: preview.optimisticTask?.executor || "VAULT-CO-PILOT",
    progress: 0,
    steps: preview.optimisticTask?.steps ?? [
      {
        id: "queue",
        name: "Queue run",
        nameAr: "إدراج المهمة في الطابور",
        status: "pending",
        description: "Submitting the approved execution plan to the live agent ledger.",
        descriptionAr: "جارٍ إرسال خطة التنفيذ المعتمدة إلى سجل الوكلاء المباشر."
      },
      {
        id: "dispatch",
        name: "Dispatch runner",
        nameAr: "تشغيل المنفذ",
        status: "pending",
        description: "Waiting for the background runner to claim the approved task.",
        descriptionAr: "بانتظار أن يلتقط المنفذ الخلفي المهمة المعتمدة."
      }
    ],
    hoursSaved: preview.optimisticTask?.hoursSaved ?? 0,
    recoveredAED: preview.optimisticTask?.recoveredAED ?? 0,
    isReported: false,
  });

  const approveExecutionPreview = async () => {
    if (!executionPreview || isApprovingExecution) return;
    const optimisticRunId = executionPreview.optimisticTask?.id ?? `agent-run-${Date.now()}`;
    const optimisticRun = buildQueuedRun(executionPreview, optimisticRunId);

    setIsApprovingExecution(true);
    setExecutionResult(`Execution result: queueing ${executionPreview.title} for ${executionPreview.matterId}.`);
    setAgentPersistenceStatus(`Execution approval submitted: ${executionPreview.title}`);

    try {
      if (USE_LOCAL_PREVIEW_SURFACE_DATA && !USE_MOCK_DATA && executionPreview.optimisticTask) {
        const persistedLocalTask = persistLocalPreviewAgentRun(executionPreview.optimisticTask);
        const nextResult = buildExecutionResult(executionPreview, persistedLocalTask.id);
        setExecutionResult(nextResult);
        setAgentPersistenceStatus(`Execution approved and queued: ${persistedLocalTask.id}`);
        writeLogs("PROMPT_ENGINE", "EXECUTION_APPROVED", nextResult, executionPreview.matterId);
        return;
      }

      if (!USE_MOCK_DATA) {
        setPendingLiveRuns((current) => [optimisticRun, ...current.filter((run) => run.id !== optimisticRun.id)]);
        const resolvedTaskId = executionPreview.taskId
          || (await matchIntentCallable({ prompt: executionPreview.prompt })).data.intent;
        const persisted = await runAgentTaskCallable({
          prompt: executionPreview.prompt,
          taskId: resolvedTaskId,
          taskLabel: executionPreview.taskLabel,
          subject: executionPreview.subject,
          matterId: executionPreview.matterId,
          params: executionPreview.params,
        });
        const persistedRun = buildQueuedRun(executionPreview, persisted.data.taskDocId);
        setPendingLiveRuns((current) => [persistedRun, ...current.filter((run) => run.id !== optimisticRun.id && run.id !== persistedRun.id)]);
        const nextResult = buildExecutionResult(executionPreview, persisted.data.taskDocId);
        setExecutionResult(nextResult);
        setAgentPersistenceStatus(`Execution approved and queued: ${persisted.data.taskDocId}`);
        writeLogs("PROMPT_ENGINE", "EXECUTION_APPROVED", nextResult, executionPreview.matterId);
        return;
      }

      if (executionPreview.optimisticTask) {
        setTasks((prev) => [executionPreview.optimisticTask as WorkspaceTask, ...prev]);
      }
      const nextResult = buildExecutionResult(executionPreview);
      setExecutionResult(nextResult);
      setAgentPersistenceStatus(`Execution approved: ${executionPreview.title}`);
      writeLogs("PROMPT_ENGINE", "EXECUTION_APPROVED", nextResult, executionPreview.matterId);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Execution approval could not be queued.";
      setPendingLiveRuns((current) => current.map((run) => {
        if (run.id !== optimisticRun.id) return run;
        return {
          ...run,
          status: "FAILED",
          steps: run.steps.map((step, index) => index === 0
            ? {
                ...step,
                status: "failed",
                description: message,
                descriptionAr: message,
              }
            : step),
        };
      }));
      setAgentPersistenceStatus(message);
      setExecutionResult(`Execution result: ${message}`);
    } finally {
      setIsApprovingExecution(false);
    }
  };

  return (
    <div data-testid="agent-workspace-page" className={`p-4 space-y-4 flex-1 overflow-y-auto premium-short-scrollbar bg-slate-55/10 bg-opacity-15 ${isAr ? "text-right" : ""}`} dir={isAr ? "rtl" : "ltr"}>
      
      {/* 1. AGENT WORKSPACE DARK RIBBON HEADER - Compact Premium Design */}
      <div className="bg-slate-900 text-white rounded-xl py-1 px-4 relative overflow-hidden shadow-md flex items-center justify-between min-h-12 select-none shrink-0 border border-slate-800">
        {/* Subtle background graphic design element */}
        <div className="absolute right-0 top-0 bottom-0 w-1/3 bg-gradient-to-l from-slate-800/15 to-transparent pointer-events-none"></div>
        <div className="absolute top-1/2 left-1/3 -translate-y-1/2 w-48 h-48 bg-slate-800/10 rounded-full blur-2xl pointer-events-none"></div>
        
        <div className="relative z-10 flex flex-row items-center justify-between w-full gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Bot className="text-amber-400 shrink-0" size={16} />
            <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1 min-w-0">
              <h1 className="text-xs md:text-sm font-display font-extrabold text-white tracking-tight leading-none truncate">
                {isAr ? "مساحة عمل العمليات الذكية" : "Agent Operations Workspace"}
              </h1>
              <span className="bg-slate-800 text-slate-300 font-bold px-1.5 py-0.5 rounded text-[8.5px] font-mono shrink-0 border border-slate-700 leading-none">
                3.5-MAX
              </span>
              <span className="hidden lg:inline-flex items-center gap-1 bg-amber-500/15 text-amber-400 border border-amber-500/20 px-2 py-0.5 rounded-full font-mono font-bold uppercase text-[8.5px] shrink-0 leading-none font-sans">
                {isAr ? "نشط" : "Operational Control"}
              </span>
            </div>
          </div>

          {/* Quick Header Action Ribbon - Sleek and Compact */}
          <div className="flex items-center gap-2 shrink-0">
            <button 
              type="button"
              onClick={() => setLibraryOpen(true)}
              aria-label="Prompts"
              className="py-1 px-2.5 bg-gradient-to-r from-amber-550 to-amber-600 hover:from-amber-610 hover:to-amber-700 text-slate-950 text-[10px] font-bold rounded-lg transition-all duration-200 cursor-pointer flex items-center justify-center gap-1 shadow-md shadow-amber-500/5 whitespace-nowrap"
            >
              <BookMarked size={11} className="text-slate-950" />
              <span>{isAr ? "مكتبة القوالب" : "Prompt Library"}</span>
            </button>
            <button 
              type="button"
              onClick={() => setActiveTab("AI_Counsel")}
              className="py-1 px-2.5 bg-slate-800 hover:bg-slate-750 text-white border border-slate-700 hover:border-slate-600 text-[10px] font-bold rounded-lg transition-all duration-200 cursor-pointer flex items-center justify-center gap-1 shadow-sm whitespace-nowrap"
            >
              <HelpCircle size={11} className="text-amber-400" />
              <span>{isAr ? "مستشار الذكاء" : "AI Counsel"}</span>
            </button>
          </div>
        </div>
      </div>

      {/* 2. Premium Tabs Bar (Aligns exactly with Case and Contract design specs) */}
      {agentPersistenceStatus && (
        <div data-testid="agent-workspace-status" className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-900 flex items-center gap-2 text-[11px] font-semibold">
          <CheckCircle size={14} className="text-amber-600" />
          <span>{agentPersistenceStatus}</span>
        </div>
      )}
      {executionPreview && (
        <div data-testid="agent-workspace-preview" className="rounded-2xl border border-slate-200 bg-white p-4 text-[11px] shadow-xs space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-[10px] font-mono font-bold uppercase tracking-wider text-slate-400">Execution Preview</div>
              <div className="text-sm font-black text-slate-900">{executionPreview.title}</div>
            </div>
            <button data-testid="agent-workspace-approve" type="button" disabled={isApprovingExecution} onClick={approveExecutionPreview} className={`rounded-xl px-4 py-2 text-xs font-bold text-white ${isApprovingExecution ? "cursor-wait bg-slate-400" : "bg-slate-900"}`}>
              {isApprovingExecution ? "Queueing..." : "Approve Execution Plan"}
            </button>
          </div>
          <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-slate-600">{executionPreview.summary}</div>
          {executionResult && <div data-testid="agent-workspace-result" className="rounded-xl border border-emerald-100 bg-emerald-50 px-3 py-2 font-semibold text-emerald-700">{executionResult}</div>}
        </div>
      )}
      {!USE_MOCK_DATA && (
        <div className="p-3 bg-white border border-slate-200 rounded-xl text-[11px] font-semibold text-slate-600">
          Agent runs: {agentRuns.sourceState} • refreshed {agentRuns.lastRefreshAt?.toLocaleTimeString() ?? "pending"}{agentRuns.error ? ` • ${agentRuns.error.message}` : ""}
        </div>
      )}

      {/* 2. Premium Tabs Bar (Aligns exactly with Case and Contract design specs) */}
      <div className="bg-white border border-slate-100/70 p-1 rounded-xl flex items-center justify-between shadow-[0_4px_22px_rgba(15,23,42,0.02)] overflow-x-auto gap-4 scrollbar-none z-10 select-none">
        <div className="flex gap-1">
          {([
            { k: "Overview", l: isAr ? "المقاييس الحية" : "Live Metrics", icon: Activity },
            { k: "ProcessStream", l: isAr ? "تدفق العمليات" : "Process Stream", icon: Terminal },
            { k: "AI_Counsel", l: isAr ? "الاستشارات الفيدرالية الفورية" : "Interactive AI Counsel", icon: HelpCircle }
          ] as const).map(tab => {
            const TabIcon = tab.icon;
            const isTabActive = activeTab === tab.k;
            return (
              <button
                key={tab.k}
                onClick={() => setActiveTab(tab.k)}
                className={`py-2.5 px-4.5 text-xs font-bold cursor-pointer transition-all duration-200 shrink-0 select-none flex items-center gap-2 rounded-xl ${
                  isTabActive 
                    ? "bg-slate-900 text-white shadow-md font-extrabold" 
                    : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                }`}
              >
                <TabIcon size={14} className={isTabActive ? "text-amber-400" : "text-slate-400"} />
                <span>{tab.l}</span>
              </button>
            );
          })}
        </div>

        {/* Dynamic status indicators on right side */}
        <div className="hidden xl:flex items-center gap-3 text-[10.5px] text-slate-500 font-medium px-4">
          <span className="flex items-center gap-1">
            <span className="h-1.5 w-1.5 bg-emerald-500 rounded-full"></span>
            <span>{isAr ? "ساعات توفير العمل:" : "Total Reclaimed:"} <strong>{visibleHoursSaved.toFixed(1)} hr</strong></span>
          </span>
          <span className="text-slate-300">|</span>
          <span className="flex items-center gap-1">
            <span className="h-1.5 w-1.5 bg-orange-500 rounded-full animate-pulse"></span>
            <span>{isAr ? "قاعدة اللوائح:" : "Auditing Laws:"} <strong>2 Active</strong></span>
          </span>
        </div>
      </div>

      {/* 3. Tab Context Routing Area */}

      {/* Tab 1: Metrics & Grounding Specs */}
      {activeTab === "Overview" && (
        <div className="space-y-6 animate-fade-in">
          
          {/* ROI Metric Strip */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 text-left">
            {/* Metric 1 */}
            <div className="bg-white rounded-2xl border border-slate-200/60 p-5 shadow-3xs relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-3 text-slate-100 opacity-60 group-hover:opacity-100 transition-opacity">
                <Flame size={44} className="text-slate-100/20" />
              </div>
              <span className="text-[9.5px] font-mono font-black text-[#64748b] block uppercase tracking-wider">
                {isAr ? "المهام الذاتية لليوم" : "TASKS ORCHESTRATED"}
              </span>
              <div className="flex items-baseline gap-2 mt-2">
                <span className="text-3xl font-black text-slate-950 tracking-tight">{visibleTasksCount}</span>
                <span className="text-[10px] font-mono font-extrabold text-[#1f2937] bg-orange-50 border border-orange-100 px-1.5 py-0.5 rounded-md">
                  +120% MoM
                </span>
              </div>
              <p className="text-[10.5px] text-slate-450 font-semibold mt-1.5">
                {isAr ? "المهام الجارية والمكتملة آلياً" : "Active and completed automated legal workflows."}
              </p>
            </div>

            {/* Metric 2 */}
            <div className="bg-white rounded-2xl border border-slate-200/60 p-5 shadow-3xs relative overflow-hidden group">
              <span className="text-[9.5px] font-mono font-black text-[#64748b] block uppercase tracking-wider">
                {isAr ? "الساعات المحفوظة تراكمياً" : "HOURS SAVED"}
              </span>
              <div className="flex items-baseline gap-2 mt-2">
                <span className="text-3xl font-black text-slate-950 tracking-tight">{visibleHoursSaved.toFixed(1)} hr</span>
                <span className="text-[10px] font-mono font-extrabold text-emerald-700 bg-emerald-50 border border-emerald-100 px-1.5 py-0.5 rounded-md">
                  {isAr ? "كفاءة مثالية" : "OPTIMAL LEVEL"}
                </span>
              </div>
              <p className="text-[10.5px] text-slate-450 font-semibold mt-1.5">
                {isAr ? "الوقت الموفر عن طريق مراجعة البنود المستقلة" : "Accrued billable hours reclaimed from manual search."}
              </p>
            </div>

            {/* Metric 3 */}
            <div className="bg-white rounded-2xl border border-slate-200/60 p-5 shadow-3xs relative overflow-hidden group">
              <span className="text-[9.5px] font-mono font-black text-[#64748b] block uppercase tracking-wider">
                {isAr ? "العائد التعاقدي المسترد لليوم" : "RECOVERED EXPENSES & LOSSES"}
              </span>
              <div className="flex items-baseline gap-2 mt-2">
                <span className="text-3xl font-black text-slate-950 tracking-tight">
                  {isAr ? `${visibleRecoveredAED.toLocaleString()} د.إ.` : `AED ${visibleRecoveredAED.toLocaleString()}`}
                </span>
                <span className="text-[10px] font-mono font-extrabold text-amber-700 bg-amber-50 border border-amber-100 px-1.5 py-0.5 rounded-md">
                  {isAr ? "تخفيف خطر ممتاز" : "RISK MITIGATED"}
                </span>
              </div>
              <p className="text-[10.5px] text-slate-450 font-semibold mt-1.5">
                {isAr ? "العقوبات الاقتصادية ومخالفات الإخطارات المستردة" : "Mitigated lease roll-overs & leakage errors detected."}
              </p>
            </div>
          </div>

          {/* Redesigned Specifications and Grounded Library */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start text-left">
            {/* Grounded Library list (8/12 widths) */}
            <div className="lg:col-span-8 bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_4px_30px_rgb(15,23,42,0.02)] space-y-5">
              <div className="border-b border-slate-100 pb-3">
                <h3 className="text-xs font-display font-bold text-slate-900 uppercase tracking-widest flex items-center gap-1.5">
                  <BookMarked size={14} className="text-slate-950" />
                  <span>{isAr ? "المواد واللوائح الموثقة نشطاً في قاعدة الصياغة" : "Grounded Statutory Sources & Federal Rules"}</span>
                </h3>
                <p className="text-[11px] text-slate-400 font-semibold mt-1">
                  {isAr ? "المستندات القانونية النشطة التي تلتزم بها ميكينة المعالجة لتجنب الأخطاء." : "Pre-loaded regulatory guidelines used by autonomous agents to verify filing structures."}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                {/* Source 1 */}
                <div className="p-4 bg-slate-50 border border-slate-200/50 rounded-xl space-y-1.5 hover:border-slate-350 transition-colors">
                  <div className={`flex justify-between items-center text-[9px] font-mono font-bold ${isAr ? "flex-row-reverse" : "flex-row"}`}>
                    <span className="text-orange-700 font-extrabold uppercase">DIFC Law Precedent</span>
                    <span className="text-slate-400">Direction 2.3</span>
                  </div>
                  <h5 className="font-extrabold text-[#0f172a] text-xs leading-snug">
                    DIFC Courts Practice Direction (Electronic Filing Signatures)
                  </h5>
                  <p className="text-[10.5px] font-semibold text-slate-500 leading-relaxed">
                    Specifies strict certificate rules for exhibits submitted on delay disputes. Missing certification results in filing blockages.
                  </p>
                  <div className={`pt-1 flex items-center gap-1.5 text-[10px] font-mono font-bold text-slate-455 hover:text-slate-805 cursor-pointer select-none ${isAr ? "flex-row-reverse text-right" : ""}`}>
                    <span>{isAr ? "تفاصيل الوثيقة" : "DIFC Docket Source"}</span>
                    <ExternalLink size={10} />
                  </div>
                </div>

                {/* Source 2 */}
                <div className="p-4 bg-slate-50 border border-slate-200/50 rounded-xl space-y-1.5 hover:border-slate-350 transition-colors">
                  <div className={`flex justify-between items-center text-[9px] font-mono font-bold ${isAr ? "flex-row-reverse" : "flex-row"}`}>
                    <span className="text-amber-700 font-extrabold uppercase">UAE Civil Code</span>
                    <span className="text-slate-400">Art 246 § 1</span>
                  </div>
                  <h5 className="font-extrabold text-[#0f172a] text-xs leading-snug">
                    UAE Civil Transactions Actions & Good Faith Standards
                  </h5>
                  <p className="text-[10.5px] font-semibold text-slate-500 leading-relaxed">
                    Mandates strict fidelity in post-execution covenant administration. Notice limits and penalty triggers audited meticulously.
                  </p>
                  <div className={`pt-1 flex items-center gap-1.5 text-[10px] font-mono font-bold text-slate-455 hover:text-slate-805 cursor-pointer select-none ${isAr ? "flex-row-reverse text-right" : ""}`}>
                    <span>{isAr ? "تفاصيل المادة القانونية" : "Federal Code Article"}</span>
                    <ExternalLink size={10} />
                  </div>
                </div>
              </div>
            </div>

            {/* Orchestration Technical Specifications (4/12 widths) */}
            <div className="lg:col-span-4 bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_4px_30px_rgb(15,23,42,0.02)] space-y-4">
              <h3 className="text-[10px] font-mono font-bold uppercase tracking-widest text-slate-400 border-b border-slate-100 pb-2">
                {isAr ? "مواصفات تشغيل الآلة المستقلة:" : "Engine Integrity Specifications"}
              </h3>
              
              <div className="space-y-3.5 text-xs">
                <div className="flex justify-between text-slate-500 font-semibold items-center">
                  <span>Scheduler Protocol:</span>
                  <span className="font-extrabold text-slate-900 font-mono text-[10px] bg-slate-50 border border-slate-200 px-2.5 py-0.5 rounded-lg">
                    {USE_MOCK_DATA ? "LIVE_SIMULATOR_V3" : "PRODUCTION_LEDGER_REQUIRED"}
                  </span>
                </div>
                
                <div className="flex justify-between text-slate-500 font-semibold items-center">
                  <span>Compliance Framework:</span>
                  <span className="font-extrabold text-slate-900 font-mono text-[10px] bg-emerald-50 text-emerald-800 border border-emerald-100 px-2.5 py-0.5 rounded-lg flex items-center gap-1">
                    <span className="h-1 w-1 rounded-full bg-emerald-500 animate-pulse"></span>
                    <span>100% SECURE BOUNDS</span>
                  </span>
                </div>

                <div className="flex justify-between text-slate-500 font-semibold items-center">
                  <span>Authorized Sandbox:</span>
                  <span className="font-mono text-[#0f172a] font-bold text-[10.5px] text-right flex items-center gap-1">
                    <span className="h-1 w-1 rounded-full bg-emerald-500 inline-block" />
                    <span>{USE_MOCK_DATA ? "DIFC_SENTINEL_ACTIVE" : "NO_SIMULATED_SANDBOX"}</span>
                  </span>
                </div>

                <div className="pt-3 border-t border-slate-100 space-y-1">
                  <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-wider block">Operational Targets:</span>
                  <p className="text-[10px] text-slate-450 leading-relaxed font-semibold">
                    Deep searches operate on litigation briefs and corporate contracts mapped on active portfolios.
                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* Tab 2: Code Streams & Dispatcher */}
      {activeTab === "ProcessStream" && (
        <div className="space-y-6 animate-fade-in text-left">
          
          {/* Dynamic Filter bar */}
          <div className={`p-4 bg-white rounded-2xl border border-slate-200/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${isAr ? "flex-row-reverse" : "flex-row"}`}>
            
            {/* Date Filters Group */}
            <div className={`flex items-center gap-2 overflow-x-auto ${isAr ? "flex-row-reverse justify-end" : "flex-row"}`}>
              <span className="text-[10px] font-mono font-black text-slate-400 shrink-0 mr-1 uppercase tracking-wider flex items-center gap-1">
                <Filter size={11} />
                <span>{isAr ? "تاريخ العمليات:" : "FILTER DATE:"}</span>
              </span>
              {([
                { k: "ALL_TIME", en: "ALL TIME", ar: "كل الوقت" },
                { k: "TODAY", en: "TODAY", ar: "اليوم" },
                { k: "YESTERDAY", en: "YESTERDAY", ar: "أمس" },
                { k: "LAST_7_DAYS", en: "LAST 7 DAYS", ar: "آخر ٧ أيام" }
              ] as const).map(f => (
                <button
                  key={f.k}
                  onClick={() => setActiveDateFilter(f.k)}
                  className={`px-3 py-1 text-[10.5px] font-bold rounded-xl border transition-all cursor-pointer whitespace-nowrap ${
                    activeDateFilter === f.k
                      ? "bg-slate-950 border-slate-950 text-white shadow-3xs font-extrabold"
                      : "bg-slate-50 hover:bg-slate-100/70 border-slate-201 text-slate-700"
                  }`}
                >
                  {isAr ? f.ar : f.en}
                </button>
              ))}
            </div>

            {/* Status Filters Group */}
            <div className={`flex items-center gap-2 overflow-x-auto ${isAr ? "flex-row-reverse justify-end" : "flex-row"}`}>
              <span className="text-[10px] font-mono font-black text-slate-400 shrink-0 mr-1 uppercase tracking-wider">
                {isAr ? "حالة المستندات:" : "STATUS:"}
              </span>
              {([
                { k: "ALL", en: "ALL", ar: "الكل" },
                { k: "COMPLETED", en: "COMPLETED", ar: "اكتمل" },
                { k: "FAILED", en: "FAILED", ar: "فشل" },
                { k: "RUNNING", en: "RUNNING", ar: "جاري" },
                { k: "QUEUED", en: "QUEUED", ar: "جدولة" }
              ] as const).map(f => (
                <button
                  key={f.k}
                  onClick={() => setActiveStatusFilter(f.k)}
                  className={`px-3 py-1 text-[10.5px] font-bold rounded-xl border transition-all cursor-pointer whitespace-nowrap ${
                    activeStatusFilter === f.k
                      ? "bg-slate-950 border-slate-950 text-white shadow-3xs font-extrabold"
                      : "bg-slate-50 hover:bg-slate-100/70 border-slate-201 text-slate-700"
                  }`}
                >
                  {isAr ? f.ar : f.en}
                </button>
              ))}
            </div>

          </div>

          {/* Action Stream Ledger Card */}
          <div className="bg-white rounded-3xl border border-slate-200/50 p-6 shadow-[0_4px_30px_rgb(15,23,42,0.02)] space-y-6">
            <div className={`flex items-center justify-between border-b border-slate-100 pb-3.5 ${isAr ? "flex-row-reverse" : "flex-row"}`}>
              <div>
                <h3 className="font-extrabold text-slate-800 text-xs font-sans tracking-wide uppercase">
                  {isAr ? "تدفق العمليات" : "Process Stream Ledger"}
                </h3>
                <p className="text-[10.5px] text-slate-400 font-semibold mt-0.5">
                  {isAr ? "الوكلاء الذاتيون يتفقدون الصلاحية وصحة البنود في الوقت الحقيقي." : "Agent process telemetry recording compliance checks, signature runs, and evidentiary dockets."}
                </p>
              </div>
              <span className="text-[10px] font-mono font-bold text-slate-450 bg-slate-50 border border-slate-150 px-2.5 py-1 rounded-full select-none">
                {filteredTasks.length} {isAr ? "مطابقات جارية" : "Matches Loaded"}
              </span>
            </div>

            {/* Action streams rendering */}
            <ActionStream
              tasks={filteredTasks}
              onRetry={handleRetryTask}
              onReport={handleReportTask}
              isAr={isAr}
            />

            {/* Dispatch Composer Input (full responsive width) */}
            <div className="pt-5 border-t border-slate-100 space-y-3">
              <span className={`text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest block ${isAr ? "text-right" : ""}`}>
                {isAr ? "أداة صياغة وتوجيه المهام الذاتية:" : "PROMPT DISPATCH COMPOSER:"}
              </span>
              <WorkspaceTaskInput
                onDispatch={handleAddNewTask}
                onOpenPromptLibrary={() => setLibraryOpen(true)}
                isAr={isAr}
              />
            </div>
          </div>
        </div>
      )}

      {/* Tab 3: Interactive consultation hub in slate-950 UI */}
      {activeTab === "AI_Counsel" && (
        <div className="bg-white rounded-3xl border border-slate-200/55 p-6 shadow-[0_4px_30px_rgb(15,23,42,0.02)] space-y-6 animate-fade-in text-left">
          
          <div className={`pb-4 border-b border-slate-150 flex flex-col md:flex-row md:items-center justify-between gap-4 ${isAr ? "flex-row-reverse text-right" : "flex-row"}`}>
            <div>
              <div className={`flex items-center gap-2 ${isAr ? "flex-row-reverse" : ""}`}>
                <div className="p-1 px-2.5 bg-orange-50 text-orange-700 border border-orange-100 rounded-lg text-[10px] font-black uppercase font-mono flex items-center gap-1 animate-pulse">
                  <Bot size={13} />
                  <span>AI Counsel</span>
                </div>
                <span className="text-xs font-display font-black text-slate-900 uppercase">
                  {isAr ? "مستشار الرقابة اللحظي واللوائح الفيدرالية" : "Interactive Regulatory Consultation Hub"}
                </span>
              </div>
              <p className="text-slate-400 text-[11px] font-semibold mt-1 leading-relaxed">
                {isAr ? "مستند إلى لوائح مركز دبي المالي المالي وقوانين المعاملات المدنية للإمارة للتدقيق وحلول الانحرافات." : "Submit contract provisions or evidentiary queries to the regulatory sentinel for instant guidance."}
              </p>
            </div>
            
            {/* Predefined prompt triggers */}
            <div className={`flex flex-wrap gap-2 text-[10.5px] text-slate-500 font-medium ${isAr ? "justify-end" : ""}`}>
              <button 
                type="button"
                onClick={() => {
                  setChatInput("What are the e-signature requirements under DIFC directions?");
                }}
                className="p-1 px-3 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl transition-all cursor-pointer font-bold"
              >
                DIFC E-Signatures
              </button>
              <button 
                type="button"
                onClick={() => {
                  setChatInput("Explain Article 246 of UAE Civil Transactions Law regard good faith.");
                }}
                className="p-1 px-3 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl transition-all cursor-pointer font-bold"
              >
                UAE Art 246 (Good Faith)
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
            
            {/* Real-time active chat console (8/12 widths) */}
            <div className="lg:col-span-8 flex flex-col justify-between border border-slate-800 rounded-2xl bg-slate-950 p-5 min-h-[480px]">
              
              <div className="flex-1 overflow-y-auto space-y-4 pr-1 text-xs mb-4 premium-short-scrollbar">
                {chatHistory.map((item, idx) => (
                  <div 
                    key={idx} 
                    className={`flex flex-col space-y-1.5 max-w-[85%] ${
                      item.sender === "user" ? "ml-auto text-right items-end animate-slice-in" : "mr-auto text-left items-start animate-slice-in"
                    }`}
                    style={{
                      marginLeft: item.sender === "user" ? "auto" : "0",
                      marginRight: item.sender === "bot" ? "auto" : "0"
                    }}
                  >
                    <span className="text-[8.5px] font-mono text-slate-500 uppercase tracking-widest font-black leading-none">
                      {item.sender === "user" ? (isAr ? "المحامي" : "YOU") : (isAr ? "مساعد الرقابة" : "AI COUNSEL")}
                    </span>
                    <div className={`p-3 rounded-2xl border leading-relaxed text-xs font-semibold ${
                      item.sender === "user" 
                        ? "bg-orange-600 border-orange-500 text-white rounded-tr-none" 
                        : "bg-slate-900 border-slate-800 text-[#22c55e] font-mono rounded-tl-none pr-4"
                    }`}>
                      {item.text}
                    </div>
                  </div>
                ))}
              </div>

              {/* Chat consultation dispatch row */}
              <form onSubmit={handleMiniChatSubmit} className={`flex items-center gap-2 pt-4 border-t border-slate-850 ${isAr ? "flex-row-reverse" : "flex-row"}`}>
                <input
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  placeholder={isAr ? "اكتب استشارتك هنا..." : "Type compliance query here (e.g. tell me about good faith principles)..."}
                  className="flex-1 bg-slate-900/60 border border-slate-800 focus:border-slate-700 text-slate-200 placeholder-slate-500 rounded-xl px-4 py-3 outline-none focus:ring-1 focus:ring-orange-600 text-xs font-semibold"
                  dir={isAr ? "rtl" : "ltr"}
                />
                <button
                  type="submit"
                  className="p-3 bg-orange-600 hover:bg-orange-700 text-white rounded-xl transition-all cursor-pointer flex items-center justify-center shrink-0 shadow-lg shadow-orange-600/15 border-transparent"
                >
                  <Send size={14} className={isAr ? "rotate-180" : ""} />
                </button>
              </form>
            </div>

            {/* Grounding and Citations Sidebar Lookups (4/12 widths) */}
            <div className="lg:col-span-4 bg-slate-50 border border-slate-250/50 p-5 rounded-2xl flex flex-col justify-between space-y-4">
              <div className="space-y-4">
                <span className={`text-[9px] font-mono font-black text-[#54647b] block uppercase tracking-widest ${isAr ? "text-right" : ""}`}>
                  {isAr ? "المرجعيات واللوائح الفيدرالية المترابطة:" : "INTELLIGENT CITATION LOOKUPS:"}
                </span>

                <div className="space-y-3 text-xs leading-relaxed">
                  <div className="p-3.5 bg-white border border-slate-200/60 rounded-xl space-y-1">
                    <span className="text-[9px] font-mono font-bold text-slate-400">DIFC REGULATORY FOCUS</span>
                    <p className="text-[10.5px] font-bold text-slate-805 leading-normal">Practice Direction 2.3</p>
                    <p className="text-[10px] font-semibold text-slate-450">
                      Telemetry checks parse digital filing certificates for evidentiary dockets submitted.
                    </p>
                  </div>

                  <div className="p-3.5 bg-white border border-slate-200/60 rounded-xl space-y-1">
                    <span className="text-[9px] font-mono font-bold text-slate-400">FEDERAL CIVIL STATUTES</span>
                    <p className="text-[10.5px] font-bold text-slate-805 leading-normal">UAE Civil Code Art. 246</p>
                    <p className="text-[10px] font-semibold text-slate-450">
                      Mandantory duty of good faith restricts aggressive opt-out window forfeitures.
                    </p>
                  </div>
                </div>
              </div>

              {/* Verified seal banner */}
              <div className="bg-white/80 p-3.5 rounded-xl border border-slate-200 text-[10.5px] font-mono font-bold text-slate-500 uppercase flex items-center gap-2 select-none">
                <CheckCircle size={14} className="text-emerald-600 shrink-0" />
                <span>SENTINEL VERIFIED OK</span>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* 4. PROMPT TEMPLATE BLUEPRINT MODAL */}
      <PromptLibrary
        isOpen={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        matters={matters}
        onLaunchAudit={handleLaunchTemplate}
        isAr={isAr}
      />

    </div>
  );
};

export default AgentWorkspacePage;
